import context from '@truckdown/components-systems';
import { Constants, ILookupService, ServiceModel, ServiceStatus, ServiceType } from '@truckdown/systems';
import { defineComponent, ref, Ref, computed, onMounted, inject, watch, PropType } from 'vue';
import {
    AddLocationAnalyticModel, ILocationActionsService,
    Listing, ListingNote, ListingPhoneNumber, ListingRatingValue,
    ListingTemplate, ListingType, PaymentType, PreferenceModel,
    PreferenceType, ThrottleResponse, NoteModel, HoursOfOperationDisplayType
} from '@truckdown/locations';

import listingMenu from './ListingMenu/ListingMenu.vue';
import ratingValue from './RatingValue/RatingValue.vue';
import listingHours from './ListingHours/ListingHours.vue';
import listingPhotos from './ListingPhotos/ListingPhotos.vue';
import listingMap from './ListingMap/ListingMap.vue';

import { AdditionalDataModel } from '@truckdown/locations/Models/AdditionalDataModel';
import { PromotionSearchService } from '@truckdown/promotions/PromotionSearchService';
import { PromotionDisplayModel } from '@truckdown/promotions/PromotionDisplayModel';
import { isCsrOrAdmin } from '../utilities';

interface IProps {
    list?: string;
    listingAccount?: string;
    listing: Listing;
    allServices: ServiceModel[];
    showDetail: boolean;
    enableMap: boolean;
    promotionId?: string;
    disableMap: boolean;
    disableDetails: boolean;
    disableActions: boolean;
    disableViewServices: boolean;
    displayNumbers: boolean;
    customMap: boolean;
    editable: boolean;
    adminView: boolean;
    isAdmin: boolean;
    selectedSite: string;
    disableRequests: boolean;
    listingTemplateType: ListingTemplate | undefined
}

interface ILinkData {
    id: string,
    accountId: string,
    nameForUrl: string,
    showRequestService: boolean,
    showEditLinks: boolean,
    showReportUpdate: boolean,
    showAdminViewLinks: boolean,
    showRemoveLink: boolean,
    showUpgradeLink: boolean,
    showViewServiceLink: boolean,
    showDetailsLink: boolean,
    showWebsiteLink: boolean,
    showPhoneNumbersLink: boolean,
    isFleet: boolean,
    isAuthenticated: boolean,
    editHoursUrl?: string,
    editUrl?: string,
    viewUrl?: string,
    detailsLink: string,
    notesLabel: string,
    preferredLabel: string,

    disableActions: boolean,
    disableMap: boolean,
    hideAllLinks: boolean
}

interface IServiceListModel {
    code: string,
    name: string,
    detail?: string,
    addFee: boolean,
    amenities: IServiceListModel[]
}

interface IPaymentImage {
    name: string,
    url: string
}

interface IData {
    preference: Ref<PreferenceModel>,
    notes: Ref<ListingNote[]>,
    ratingValue: Ref<ListingRatingValue>,
    panelDisplay: Ref<string>,
    numbers: Ref<ThrottleResponse<ListingPhoneNumber[]> | undefined>,
    displayNumbers: Ref<boolean>,
    initialRating: Ref<number>,
    promotion: Ref<PromotionDisplayModel | null>,
    showPromo: Ref<boolean>,
    ServiceList: Ref<ServiceModel[]>,

    displayRequest: Ref<boolean>,

    listingType: Ref<ListingType>,
    listingTemplate: Ref<ListingTemplate>,
    logoUrl: Ref<string>
}

const isOpen = function (listing: Listing) {
    return listing.hours.type == HoursOfOperationDisplayType.Open
        || listing.hours.type == HoursOfOperationDisplayType.AfterHours;
}

const showRequestButton = function (props: IProps) {
    if (props.disableRequests) {
        return false;
    }

    if (context.hasClaim('FSRDISABLED')) {
        return false;
    }

    //listing is premium and is open
    if (props.listing.properties.type > 1
        && isOpen(props.listing)
        && props.listing.properties.canAcceptServiceRequests) {

        return true;
    }

    return false;
}

const getInitialData = function (props: IProps): IData {
    props.listing.notes = props.listing.notes.sort((a, b) => {
        if (new Date(a.date) < new Date(b.date)) {
            return 1;
        }
        if (new Date(a.date) > new Date(b.date)) {
            return -1;
        }
        return 0;
    });

    return {
        preference: ref<PreferenceModel>(props.listing.properties.preference),
        notes: ref<ListingNote[]>(props.listing.notes),
        ratingValue: ref<ListingRatingValue>(props.listing.properties.rating),
        panelDisplay: ref<string>(''),
        numbers: ref<ThrottleResponse<ListingPhoneNumber[]> | undefined>(props.listing.phoneNumbers.length > 0 ? {
            blocked: false,
            captcha: false,
            warning: '',
            result: props.listing.phoneNumbers
        } : undefined),

        displayNumbers: ref<boolean>(props.displayNumbers && props.listing.phoneNumbers.length > 0),
        initialRating: ref<number>(0),
        promotion: ref<PromotionDisplayModel | null>(null),
        showPromo: ref<boolean>(false),
        ServiceList: ref<ServiceModel[]>(props.allServices),

        displayRequest: ref<boolean>(showRequestButton(props)),

        //template selector
        listingType: ref<ListingType>(getListingType(props.listingTemplateType) || props.listing.properties.type),
        listingTemplate: ref<ListingTemplate>(props.listingTemplateType || props.listing.properties.template),
        logoUrl: ref<string>(props.listing.properties.logoUrl)
    };
}


const getListingType = function (listingTemplateType?: ListingTemplate) {
    if (listingTemplateType != null && listingTemplateType != undefined ) {
        switch (listingTemplateType) {
            case ListingTemplate.Elite: {
                return ListingType.Elite;
            }
            case ListingTemplate.Pro: {
                return ListingType.Pro;
            }
            case ListingTemplate.Branded: {
                return ListingType.Branded;
            }
            default:
                return ListingType.Stock;
            
        }
    }
}

const getAmenity = function (listing: Listing, service: ServiceModel): IServiceListModel {
    let addData = listing.additionalData
        .filter((d) => { return d.isAmentity && d.key == service.code; });

    if (addData.length > 0 && addData[0].value && addData[0].value !== null) {
        return {
            code: service.code,
            name: service.name,
            detail: addData[0].value,
            addFee: addData[0].additionalFee,
            amenities: []
        };
    }
    return {
        code: service.code,
        name: service.name,
        addFee: false,
        amenities: []
    };
}

const getAmenities = function (listing: Listing, service: ServiceModel, allServices: ServiceModel[]): IServiceListModel[] {
    if (service.children.length == 0) {
        return [];
    }

    return allServices.filter((s) => {
        return s.type == ServiceType.Amenity
            && service.children.some((l) => { return l == s.code; })
            && listing.services.some((l) => { return l.code == s.code; });
    }).map((s) => {
        return getAmenity(listing, s);
    });
}

const getServiceListModels = function (listing: Listing, allServices: ServiceModel[]): IServiceListModel[] {
    return allServices.filter((s) => {
        return s.type == ServiceType.Service
            && listing.services.some((l) => { return l.code == s.code; });
    }).map((s) => {
        return {
            code: s.code,
            name: s.name,
            addFee: false,
            amenities: getAmenities(listing, s, allServices)
        } as IServiceListModel;
    });
}

const getDealerNetworks = function (listing: Listing, allServices: ServiceModel[]): IServiceListModel[] {
    return allServices.filter((s) => {
        return s.type == ServiceType.DealerNetwork
            && listing.networks.some((l) => { return l.code == s.code; });
    }).map((s) => {
        return {
            code: s.code,
            name: s.name,
            addFee: false,
            amenities: []
        } as IServiceListModel;
    });
}

const getListingDetails = function (listing: Listing): AdditionalDataModel[] {
    return listing.additionalData.filter((d) => {
        return !d.isAmentity;
    });
}

const getPaymentImage = function (type: PaymentType): IPaymentImage {
    switch (type) {
        case PaymentType.AmericanExpress: {
            return {
                name: 'American Express',
                url: '/listings/locations-components/img/americanexpress.png'
            };
        }
        case PaymentType.cbCharge: {
            return {
                name: 'cbCharge',
                url: '/listings/locations-components/img/cbcharge.png'
            };
        }
        case PaymentType.ComData: {
            return {
                name: 'ComData',
                url: '/listings/locations-components/img/comdata.png'
            };
        }
        case PaymentType.Discover: {
            return {
                name: 'Discover',
                url: '/listings/locations-components/img/discover.png'
            };
        }
        case PaymentType.EFS: {
            return {
                name: 'EFS',
                url: '/listings/locations-components/img/efs.png'
            };
        }
        case PaymentType.Mastercard: {
            return {
                name: 'Mastercard',
                url: '/listings/locations-components/img/mastercard.png'
            };
        }
        default: {
            return {
                name: 'Visa',
                url: '/listings/locations-components/img/visa.png'
            };
        }
    }
}

const isPremiumListing = function (listingType: ListingType): boolean {
    return listingType == ListingType.Branded
        || listingType == ListingType.Pro
        || listingType == ListingType.Elite;
}

const isAdmin = context.isInRole(Constants.Roles.CSRRole)
    || context.isInRole(Constants.Roles.SystemRole);

const isOwnedListing = function (listing: Listing) {
    return context.isAuthenticated
    && context.isInRole(Constants.Roles.CustomerAdminRole)
    && (listing.accountId == context.user?.accountId
        || listing.accountIds.some((a) => {
            return a == context.user?.accountId
        })
    );
}

const getListingLinks = function (nameForUrl: string, props: IProps, data: IData): ILinkData {
    var isOwned = isOwnedListing(props.listing);

    var isPublished = data.listingType.value != ListingType.None;

    var isPremium = isPremiumListing(data.listingType.value);

    return {
        id: props.listing.id,
        accountId: props.listing.accountId,
        nameForUrl: nameForUrl,
        showRequestService: data.displayRequest.value,
        showAdminViewLinks: isAdmin,
        showEditLinks: isAdmin || (isOwned && props.listingAccount == props.listing.accountId),
        showDetailsLink: (isAdmin || isOwned || isPremium) && !props.showDetail && !props.disableDetails,
        showRemoveLink: isOwned && isPublished,
        showUpgradeLink: isOwned && data.listingType.value != ListingType.Elite,
        showReportUpdate: !isAdmin && !isOwned,
        showWebsiteLink: props.showDetail && isPremium && props.listing.properties.website !== undefined && (props.listing.properties.website.length > 0),
        showViewServiceLink: isPremium && !props.showDetail && !props.disableViewServices,
        showPhoneNumbersLink: !data.displayNumbers.value,
        isFleet: context.isInRole(Constants.Roles.FleetUserRole),
        isAuthenticated: context.isAuthenticated,
        editHoursUrl: isAdmin ? '/listings/admin/view-location/' + encodeURIComponent(props.listing.id) + "#edit-hours" : '/listings/view-location/' + encodeURIComponent(props.listing.id) + "#edit-hours",
        editUrl: isAdmin ? '/listings/admin/view-location/' + encodeURIComponent(props.listing.id) + "#edit" : '/listings/view-location/' + encodeURIComponent(props.listing.id) + "#edit",
        viewUrl: isAdmin ? '/listings/admin/view-location/' + encodeURIComponent(props.listing.id) : '/listings/view-location/' + encodeURIComponent(props.listing.id),
        detailsLink: isOwned && props.listingAccount == props.listing.accountId && props.list === "list" ? `/listings/view-location/${encodeURIComponent(props.listing.id)}` :
            isAdmin && props.list === "list" ? `/listings/admin/view-location/${encodeURIComponent(props.listing.id)}` : `/search/view-detail/${nameForUrl}/${props.listing.id}`,
        notesLabel: data.notes.value.length == 0 ? 'Add Note' : 'View Notes',
        preferredLabel: data.preference.value.type == PreferenceType.Preferred ? 'Edit Favorite Settings' : 'Add to Favorites List',

        disableActions: props.disableActions,
        disableMap: props.disableMap,
        hideAllLinks: props.editable
    };
}

function stripHtmlKeepLineBreaks(htmlString: string) {
    // Replace <br>, <br/>, <br /> with a newline character
    let processedString = htmlString
        // Replace <br>, <br/>, <br /> with a newline character
        .replace(/<br\s*\/?>/gi, '\n')

        // Replace </p> with a newline character;
        .replace(/<\/p>/gi, '\n')

        // Replace </li> with a newline character;
        .replace(/<\/li>/gi, '\n')

        // Replace </ul> with a newline character;
        .replace(/<\/ul>/gi, '\n')
        .replace(/<ul>/gi, '\n')

        // Replace </ol> with a newline character;
        .replace(/<\/ol>/gi, '\n')
        .replace(/<ol>/gi, '\n');

    // Remove all other HTML tags
    processedString = processedString.replace(/<[^>]+>/g, '');

    return processedString;
}

const getComputed = function (props: IProps, data: IData) {
    let nameForUrl = (props.listing.name + ' ' + props.listing.location.city + ' ' + props.listing.location.regionCode)
        .toLowerCase()
        .replace(/[^a-z \+]/g, '')
        .replace(/[ \+]/g, '_');

    return {
        isPremium: computed(() => {
            return isPremiumListing(data.listingType.value);
        }),
        mainClass: computed(() => {
            let classes: string[] = ['cmp-listing'];
            if (isPremiumListing(data.listingType.value)) {
                classes.push('premium');
            }
            if (data.promotion.value) {
                classes.push('promo');
            }

            if (props.editable) {
                classes.push('editable');
            }

            if (props.isAdmin) {
                classes.push('is-admin');
            }

            if (props.selectedSite && props.selectedSite.toLowerCase() == 'planrv' ) {
                classes.push('planrv');
            }

            if (props.list && props.list == "list") {
                classes.push('admin-list');
            }
            switch (data.listingTemplate.value) {

                case ListingTemplate.Elite: {
                    classes.push('elite');
                    break;
                }
                case ListingTemplate.TDPreferred: {
                    classes.push('preferred');
                    break;
                }
                case ListingTemplate.Pro: {
                    classes.push('pro');
                    break;
                }
                case ListingTemplate.Branded: {
                    classes.push('branded');
                    break;
                }
                default: {
                    classes.push('stock');
                    break;
                }
            }

            if (props.listing.properties.customClass && props.listing.properties.customClass.length > 0) {
                classes.push(props.listing.properties.customClass);
            }

            return classes.join(' ');
        }),
        preferredLabel: computed(() => {
            if (data.preference.value.type == PreferenceType.Preferred) {
                return 'Edit Favorite Settings';
            }
            return 'Add to Favorites List';
        }),
        viewUrl: computed(() => {
            if (isCsrOrAdmin() || (isOwnedListing(props.listing) && props.listingAccount == props.listing.accountId) && props.list === "list") {
                return isOwnedListing(props.listing) ? '/listings/view-location/' + encodeURIComponent(props.listing.id) : '/listings/admin/view-location/' + encodeURIComponent(props.listing.id);
            } else {
                return '/search/view-detail/'
                    + encodeURIComponent(nameForUrl)
                    + '/' + encodeURIComponent(props.listing.id);
            }
        }),
        displayName: computed(() => {
            if (props.listing.properties.private)
                return props.listing.name + ' (private)';
            if (data.listingType.value == ListingType.None)
                return props.listing.name + ' (not published)';
            return props.listing.name;
        }),
        cleanDescription: computed(() => {
            if (props.listing.properties.description && props.listing.properties.description.trim().length > 0) {
                return stripHtmlKeepLineBreaks(props.listing.properties.description);
            }
            return props.listing.name;
        }),
        services: computed(() => {
            return getServiceListModels(props.listing, data.ServiceList.value);
        }),
        networks: computed(() => {
            return getDealerNetworks(props.listing, data.ServiceList.value);
        }),
        details: computed(() => {
            return getListingDetails(props.listing);
        }),
        paymentImages: computed(() => {
            return props.listing.properties.paymentTypes.map((t) => getPaymentImage(t));
        }),
        icon: computed(() => {
            if (props.listing.properties.useCustomIcon) {
                if (props.listing.properties.customClass == "msp")
                    return "/listings/locations-components/img/type_msp.png";

                if (props.listing.properties.customClass == "mspar")
                    return "/listings/locations-components/img/type_mspar.png";
            }

            switch (data.listingType.value) {
                case ListingType.Elite: {
                    return '/listings/locations-components/img/type_elite.png';
                }

                case ListingType.Pro: {
                    return '/listings/locations-components/img/type_pro.png';
                }

                case ListingType.Branded: {
                    return '/listings/locations-components/img/type_brand.png';
                }

                default: {
                    return '';
                }
            }
        }),
        noteLabel: computed(() => {
            if (data.notes.value.length == 0) {
                return 'Add Note';
            }
            return 'View Notes';
        }),
        noteCount: computed(() => {
            if (data.notes.value.length == 0) {
                return '';
            }
            if (data.notes.value.length > 9) {
                return '9+';
            }
            return '' + data.notes.value.length;
        }),
        listingLinks: computed(() => {
            return getListingLinks(nameForUrl, props, data);
        }),
        promotionTitle: computed(() => {
            let title = data.promotion.value?.title ?? '';
            if (title.length < 135) {
                return title;
            } else {
                return title.substring(0, 132) + "...";
            }
        }),
        enableDetailsLink: computed(() => {
            return !props.disableDetails && (isAdmin || isOwnedListing(props.listing))
        }),
        showVerifiedDate: computed(() => {
            return props.list === "list" && props.isAdmin;
        }),
        showBillingAccBadge: computed(() => {
            return (props.listingAccount != props.listing.accountId) && props.list === "list" && !props.isAdmin;
        })
    };
}

const submitAnalytics = async function (listing: Listing, fact: string) {
    var model: AddLocationAnalyticModel = {
        locationId: listing.id,
        cube: 'location',
        facts: [
            { "n": fact, "v": 1 }
        ],
        dimensions: [],
        drilledDimensions: [],
    };

    context.getService<ILocationActionsService>('ILocationActionsService')
        .then((service) => {
            service.addLocationAnalytic(model)
                .catch((reason) => {
                    console.log(reason);
                });
        })
        .catch((reason) => {
            console.log(reason);
        });
}

const showMap = async function (listing: Listing) {
    try {
        let service = await context.getService<ILocationActionsService>('ILocationActionsService');
        await service.showOnMap(listing.id);
        context.analytics({
            'event': 'Map',
            'event_category': 'Listing',
            'event_label': 'Show'
        });
    }
    catch (err) {
        console.log(err);
    }
}

const setPanel = async function (listing: Listing, data: IData, panel: string) {
    if (data.panelDisplay.value == panel) {
        data.panelDisplay.value = '';
    } else {
        if (panel == 'map') {
            await showMap(listing);
        }

        switch (panel) {
            case 'forward': {
                await submitAnalytics(listing, 'bf');
                break;
            }
            case 'rate': {
                await submitAnalytics(listing, 'br');
                break;
            }
            case 'notes': {
                await submitAnalytics(listing, 'bn');
                break;
            }
            case 'favorite': {
                await submitAnalytics(listing, 'bfav');
                break;
            }
            case 'avoid': {
                await submitAnalytics(listing, 'bav');
                break;
            }
            case 'services': {
                await submitAnalytics(listing, 'bsvs');
                break;
            }
            default: {
                break;
            }
        }

        data.panelDisplay.value = panel;
    }
}

var isLoadingNumbers = false;
const showNumbers = async function (listing: Listing, data: IData) {
    if (!isLoadingNumbers) {
        try {
            isLoadingNumbers = true;
            let service = await context.getService<ILocationActionsService>('ILocationActionsService');
            let res = await service.addCall(listing.id);
            data.numbers.value = res;
            if (!res.blocked && !res.captcha) {
                context.analytics({
                    'event': 'Call',
                    'event_category': 'Listing',
                    'event_label': 'Call'
                });
                context.events.emit('add-call-count');
            }
            context.events.emit('show-numbers', listing.id);
            data.displayNumbers.value = true;
        }
        catch (err) {
            console.log(err);
        }
        finally {
            isLoadingNumbers = false;
        }
    }
}


var isLoading = false;
const viewWebsite = async function (listing: Listing) {

    if (!isLoadingNumbers) {
        try {
            isLoadingNumbers = true;
            let service = await context.getService<ILocationActionsService>('ILocationActionsService');
            await service.viewWebsite(listing.id);
            window.open(listing.properties.website);
        }
        catch (err) {
            console.log(err);
        }
        finally {
            isLoading = false;
        }
    }
}

const getMethods = function (data: IData, props: IProps, ctx: any) {
    const smoothScroll = inject('smoothScroll') as any;

    return {
        moveToFavorite: function (preference: PreferenceModel) {
            if (preference != null) {
                data.panelDisplay.value = '';
                data.preference.value = preference;
                props.listing.properties.preference = preference;
            }
            if (props.list && props.list !== 'favorite') {
                console.log("GS emit move-listing");
                context.events.emit('move-listing', props.list, 'favorite', props.listing);
            } else {
                console.log("GS don't emite move-listing");
            }
        },
        moveFromFavorite: function () {
            data.panelDisplay.value = '';
            data.preference.value = {
                type: PreferenceType.None,
                services: []
            } as PreferenceModel;
            props.listing.properties.preference = data.preference.value;
            if (props.list && props.list !== 'listings') {
                context.events.emit('move-listing', props.list, 'listings', props.listing);
            }
        },
        addAvoid: function (preference: PreferenceModel) {
            if (preference != null) {
                data.panelDisplay.value = '';
                data.preference.value = preference;
                props.listing.properties.preference = preference;
            }
            if (props.list) {
                context.events.emit('move-listing', props.list, 'avoid', props.listing);
            }
        },
        removeAvoid: function () {
            data.panelDisplay.value = '';
            data.preference.value = {
                type: PreferenceType.None,
                services: []
            } as PreferenceModel;
            props.listing.properties.preference = data.preference.value;

            if (props.list && props.list == 'avoid') {
                context.events.emit('move-listing', props.list, 'listings', props.listing);
            }
        },
        updateFavorite: function (preference: PreferenceModel) {
            if (preference != null) {
                data.panelDisplay.value = '';
                data.preference.value = preference;
                props.listing.properties.preference = preference;
            }
        },
        showMap: async function (ev: Event) {
            if (props.showDetail || props.enableMap) {
                await setPanel(props.listing, data, 'map');
            }
            else {
                if (props.customMap || props.isAdmin) {
                    if (data.panelDisplay.value == 'map') {
                        data.panelDisplay.value = '';
                    } else {
                        data.panelDisplay.value = 'map';
                        context.events.emit('show-map', props.listing.id);
                    }
                } else {
                    await showMap(props.listing);
                    context.events.emit('show-map', props.listing.id);
                    let el = document.getElementById('activity-board');
                    smoothScroll({
                        scrollTo: el,
                        hash: '#activity-board',
                        offset: -50
                    });
                }
            }
        },
        showForward: async function (ev: Event) {
            await setPanel(props.listing, data, 'forward');
        },
        showRatings: async function (ev: Event) {
            await setPanel(props.listing, data, 'rate');
        },
        openRating: async function (count: number) {
            data.initialRating.value = count;
            await setPanel(props.listing, data, 'rate');
        },
        showNotes: async function (ev: Event) {
            await setPanel(props.listing, data, 'notes');
        },
        showFavorite: async function (ev: Event) {
            await setPanel(props.listing, data, 'favorite');
        },
        showAvoid: async function (ev: Event) {
            await setPanel(props.listing, data, 'avoid');
        },
        showReportUpdate: async function (ev: Event) {
            await setPanel(props.listing, data, 'report-update');
        },
        showRemoveListing: async function (ev: Event) {
            await setPanel(props.listing, data, 'remove-listing');
        },
        showUpgradeListing: async function (ev: Event) {
            await setPanel(props.listing, data, 'upgrade-listing');
        },
        showServices: async function (ev: Event) {
            await setPanel(props.listing, data, 'services');
        },
        viewWebsite: async function (ev: Event) {
            await viewWebsite(props.listing);
        },
        showNumbers: async function (ev: Event) {
            if (data.numbers.value
                && data.numbers.value.result !== null
                && data.numbers.value.result.length > 0) {
                context.events.emit('show-numbers', props.listing.id);
                data.displayNumbers.value = true;
            } else {
                await showNumbers(props.listing, data);
            }
        },
        getButtonClass: function (panel: string, base: string) {
            let suffix = '';
            if (data.panelDisplay.value == panel) {
                suffix = suffix + ' active';
            }
            if (panel != 'map') {
                if (!context.isAuthenticated) {
                    suffix = suffix + ' disabled';
                } else {
                    if (panel == 'favorite'
                        || panel == 'avoid') {
                        if (!context.isInRole(Constants.Roles.FleetUserRole)) {
                            suffix = suffix + ' disabled';
                        }
                    }
                }
            }
            return base + suffix;
        },
        addNote: function (note: NoteModel) {
            let listingNote = {
                id: note.id,
                userId: note.userId,
                user: note.user,
                comment: note.comment,
                date: note.modified
            } as ListingNote;

            props.listing.notes.unshift(listingNote);
            data.notes.value = props.listing.notes;
        },
        editNote: function (note: NoteModel) {
            let listingNote = {
                id: note.id,
                userId: note.userId,
                user: note.user,
                comment: note.comment,
                date: note.modified
            } as ListingNote;

            props.listing.notes = props.listing.notes
                .filter((itm) => itm.id != listingNote.id);
            props.listing.notes.unshift(listingNote);
            data.notes.value = props.listing.notes;
        },
        deleteNote: function (noteId: string) {
            props.listing.notes = props.listing.notes
                .filter((itm) => itm.id != noteId);
            data.notes.value = props.listing.notes;
        },
        getAmenityDisplay: function (amenity: IServiceListModel) {
            let suffix = '';
            if (amenity.addFee) {
                suffix = '$';
            }
            if (amenity.detail && amenity.detail.length > 0) {
                if (suffix.length > 0) {
                    suffix = suffix + ' ' + amenity.detail;
                } else {
                    suffix = amenity.detail;
                }
            }
            if (suffix.length > 0) {
                return amenity.name + ' (' + suffix + ')';
            }
            return amenity.name;
        },
        togglePromoModal: async function () {
            if (data.promotion.value && data.promotion.value.message.length == 0) {
                await getPromotion(data, data.promotion.value.id, props.listing?.id || '');
            } else {
                data.showPromo.value = !data.showPromo.value;
            }
        },
        openEditModal: function (type: string, step?: number, section?: string) {
            ctx.emit('open-edit-modal', type, step || 1, section || "");
        },
        openHours: function () {
            window.scrollTo({ top: 0, behavior: "smooth" });
            window.location.hash = 'edit-hours';
        }
    };
}

const getPromotion = async function (data: IData, id: string, locationId: string) {
    try {
        let promotionsService = new PromotionSearchService(context);
        let res = await promotionsService.viewPromo(id, locationId);
        if (res) {
            data.promotion.value = res;
            data.showPromo.value = !data.showPromo.value
        }
    } catch (err) {
        console.log(err);
    }
}

const loadServices = async function (data: IData) {
    try {
        let service = await context.getService<ILookupService>('ILookupService');
        let services = await service.getServices({
            types: [ServiceType.Service, ServiceType.DealerNetwork, ServiceType.Amenity],
            status: ServiceStatus.Active,
            sites: [context.system]
        });
        if (services && services.length > 0) {
            data.ServiceList.value = services;
        }
    }
    catch (err) {
        console.log(err);
    }
}

export default defineComponent({
    name: 'listing',
    components: {
        'listing-menu': listingMenu,
        'rating-value': ratingValue,
        'listing-hours': listingHours,
        'listing-photos': listingPhotos,
        'listing-map': listingMap
    },
    props: {
        list: String,
        listing: {
            type: Object as () => Listing,
            required: true
        },
        allServices: {
            type: Array as () => ServiceModel[],
            required: true,
            default: []
        },
        showDetail: {
            type: Boolean,
            default: false
        },
        enableMap: {
            type: Boolean,
            default: false
        },
        promotionId: String,

        disableMap: {
            type: Boolean,
            default: false
        },
        disableDetails: {
            type: Boolean,
            default: false
        },
        disableActions: {
            type: Boolean,
            default: false
        },
        disableViewServices: {
            type: Boolean,
            default: false
        },
        displayNumbers: {
            type: Boolean,
            default: false
        },
        customMap: {
            type: Boolean,
            default: false
        },
        disableRequests: {
            type: Boolean,
            default: true
        },
        customData: {
            type: Boolean,
            default: false
        },
        editable: {
            type: Boolean,
            default: false,
        },
        adminView: {
            type: Boolean,
            default: false,
        },
        isAdmin: {
            type: Boolean,
            default: false,
            required: false
        },
        listingTemplateType: {
            type: Number as PropType<ListingTemplate>,
            required: false  
        },
        selectedSite: {
            type: String,
            required: false,
            default: context.system
        },
        listingAccount: {
            type: String,
            required: false,
        },
        overrideDefault: {
            type: Boolean,
            required: false,
            default: false,
        }
    },
    emits: ['show-map', 'open-edit-modal'],
    setup: function (props, ctx) {
        let listing = props.listing;

        let data = getInitialData(props);
        let computedValues = getComputed(props, data);
        let methods = getMethods(data, props, ctx);

        if (props.promotionId) {
            data.promotion.value = {
                id: props.promotionId,
                title: 'Promotion Available',
                message: ''
            } as PromotionDisplayModel;
        }

        watch(() => props.promotionId, () => {
            if (props.promotionId) {
                data.promotion.value = {
                    id: props.promotionId,
                    title: 'Promotion Available',
                    message: ''
                } as PromotionDisplayModel;
            } else {
                data.promotion.value = null;
            }
        });

        watch(() => props.listing.properties.logoUrl, () => {
            if (props.listing.properties.logoUrl) {
                data.logoUrl.value = props.listing.properties.logoUrl;
            } 
        });

        watch(() => props.listingTemplateType, () => {
            if (props.listingTemplateType != null && props.listingTemplateType != undefined) {
                data.listingTemplate.value = props.listingTemplateType;
                data.listingType.value = getListingType(props.listingTemplateType) ?? data.listingType.value;
            } else {
                data.listingType.value = listing.properties.type;
                data.listingTemplate.value = listing.properties.template;
                data.logoUrl.value = listing.properties.logoUrl;
            }
        });

        onMounted(async () => {
            if (data.ServiceList.value.length == 0) {
                await loadServices(data);
            }

            if (props.showDetail) {
                context.analytics({
                    'event': 'View',
                    'event_category': 'Listing',
                    'event_label': 'Details'
                });
            }
        });

        context.events.on('show-numbers', (id: string) => {
            if (id !== listing.id) {
                data.displayNumbers.value = false;
            }
        });

        return {
            ...data,
            ...computedValues,
            ...methods
        };
    }
});